import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { QuestionTypeCategory } from '@/contracts/questionnaire'
import type { QuestionType } from '@/types/form'
import { Search } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import {
  type QuestionTypeMeta,
  questionTypeCategoryMeta,
  questionTypeMeta,
  searchQuestionTypes,
} from './question-type-meta'

type QuestionTypeSelectorCategory = QuestionTypeCategory | 'all'

const questionTypeSelectorCategories: Array<{
  value: QuestionTypeSelectorCategory
  label: string
  description: string
}> = [
  { value: 'all', label: '全部', description: '所有可加入問卷的題型。' },
  ...questionTypeCategoryMeta,
]

function getQuestionTypeCategoryCount(value: QuestionTypeSelectorCategory) {
  if (value === 'all') return questionTypeMeta.length
  return questionTypeMeta.filter((meta) => meta.category === value).length
}

export function QuestionTypeSelector({
  trigger,
  onSelect,
}: {
  trigger?: ReactNode
  onSelect: (type: QuestionType) => void
}) {
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [activeCategory, setActiveCategory] = useState<QuestionTypeSelectorCategory>('all')
  const filteredTypes = useMemo(() => searchQuestionTypes(keyword), [keyword])
  const visibleTypes = filteredTypes.filter(
    (meta) => activeCategory === 'all' || meta.category === activeCategory,
  )
  const activeCategoryMeta =
    questionTypeSelectorCategories.find((category) => category.value === activeCategory) ??
    questionTypeSelectorCategories[0]

  function choose(type: QuestionType) {
    onSelect(type)
    setOpen(false)
    setKeyword('')
    setActiveCategory('all')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button className="w-full">+ 新增問題</Button>}
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0"
      >
        <div className="flex h-full min-h-0 flex-col bg-slate-50">
          <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 pr-14">
            <DialogHeader>
              <DialogTitle>選擇問題類型</DialogTitle>
            </DialogHeader>
          </div>

          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <aside className="flex max-h-64 shrink-0 flex-col border-b border-slate-200 bg-white md:max-h-none md:w-64 md:border-b-0 md:border-r">
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="text-sm font-semibold text-slate-950">題型分類</p>
              </div>
              <ScrollArea className="min-h-0 flex-1" viewportClassName="p-3">
                <div
                  aria-label="題型分類"
                  aria-orientation="vertical"
                  className="grid gap-2"
                  role="tablist"
                >
                  {questionTypeSelectorCategories.map((category) => {
                    const selected = activeCategory === category.value
                    return (
                      <button
                        key={category.value}
                        type="button"
                        role="tab"
                        aria-controls={`question-type-panel-${category.value}`}
                        aria-selected={selected}
                        id={`question-type-tab-${category.value}`}
                        onClick={() => setActiveCategory(category.value)}
                        className={`group flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                          selected
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-950 shadow-sm'
                            : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-950'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block font-semibold">{category.label}</span>
                          <span
                            className={`mt-0.5 block truncate text-xs ${
                              selected ? 'text-emerald-700' : 'text-slate-500'
                            }`}
                          >
                            {category.description}
                          </span>
                        </span>
                        <Badge
                          variant={selected ? 'default' : 'secondary'}
                          className={selected ? 'shrink-0 bg-emerald-600 text-white' : 'shrink-0'}
                        >
                          {getQuestionTypeCategoryCount(category.value)}
                        </Badge>
                      </button>
                    )
                  })}
                </div>
              </ScrollArea>
            </aside>

            <section
              aria-label="題型主畫面"
              className="flex min-w-0 flex-1 flex-col"
              id={`question-type-panel-${activeCategory}`}
            >
              <div className="shrink-0 border-b border-slate-200 bg-white p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-base font-semibold text-slate-950">
                        {activeCategoryMeta.label}
                      </h3>
                      <Badge variant="secondary">{visibleTypes.length} 種</Badge>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{activeCategoryMeta.description}</p>
                  </div>
                  <div className="relative w-full max-w-xl lg:w-[420px]">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜尋題型、用途或英文代碼..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <ScrollArea className="min-h-0 flex-1" viewportClassName="p-5">
                {visibleTypes.length > 0 ? (
                  <QuestionTypeGrid items={visibleTypes} onChoose={choose} />
                ) : (
                  <EmptyQuestionTypeState />
                )}
              </ScrollArea>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function EmptyQuestionTypeState() {
  return (
    <div className="grid min-h-44 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center">
      <div>
        <h4 className="font-medium text-slate-900">沒有符合條件的題型</h4>
        <p className="mt-1 text-sm text-slate-500">請調整搜尋關鍵字。</p>
      </div>
    </div>
  )
}

function QuestionTypeGrid({
  items,
  onChoose,
}: {
  items: QuestionTypeMeta[]
  onChoose: (type: QuestionType) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {items.map((meta) => {
        const Icon = meta.icon
        return (
          <button
            key={meta.type}
            type="button"
            onClick={() => onChoose(meta.type)}
            className="group flex min-h-28 items-start gap-4 rounded-lg border border-slate-200 bg-white p-4 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <div className="grid size-12 shrink-0 place-items-center rounded-md bg-indigo-100 text-indigo-700 transition-colors group-hover:bg-indigo-600 group-hover:text-white">
              <Icon className="size-6" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-950">{meta.zhName}</span>
                {!meta.answerable && <Badge variant="secondary">不需填答</Badge>}
              </div>
              <p className="mt-1 text-sm leading-5 text-slate-500">{meta.description}</p>
            </div>
          </button>
        )
      })}
    </div>
  )
}
